import { AxiosResponse } from "axios";
import { Project } from "../models/Project";
import { SERVERURL } from "../globals/constants";
import { Profile } from "../models/Profile";
import { Contact } from "../models/Contact";
import { ProjectStore } from "../stores/useProject";
import { Set } from "../models/global/Set";
import axios from "./axiosConfig";
import { Directory } from "../models/Directory";
import { File as FileEntity} from "../models/File";

export const getAllProjects = async (set: Set<ProjectStore>, user: Profile) => {
  const response = await axios.get<Project[]>(`${SERVERURL}/project/getall`, {
    headers: {
      Authorization: `Bearer ${user.token}`,
      "Content-Type": "application/json;charset=UTF-8",
    },
  });

  set((state) => ({ ...state, projects: response.data }));

  return response.data;
};

export const getProject = async (user: Profile, projectId: number) => {
  const { data } = await axios.get<Project>(
    `${SERVERURL}/project/get/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const createProject = async (
  user: Profile,
  project: Project
): Promise<number> => {
  try {
    const response: AxiosResponse = await axios.post(
      `${SERVERURL}/project/create`,
      project,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const updateProject = async (
  user: Profile,
  project: Project,
  projectId: number
): Promise<number> => {
  try {
    const response: AxiosResponse = await axios.put(
      `${SERVERURL}/project/update/${projectId}`,
      project,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const deleteProject = async (user: Profile, projectId: number) => {
  try {
    const response: AxiosResponse = await axios.delete(
      `${SERVERURL}/project/remove/${projectId}`,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const addContactToProject = async (
  user: Profile,
  projectId: number,
  contact: Contact
): Promise<number> => {
  try {
    const response: AxiosResponse = await axios.patch(
      `${SERVERURL}/project/addContactToProject/${projectId}`,
      contact,
      {
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${user.token}`,
        },
      }
    );
    return response.status;
  } catch (error: any) {
    if (error.response) {
      return error.response.status;
    } else {
      return Promise.reject(error.message);
    }
  }
};

export const getRootDirectory = async (user: Profile, projectId: number) => {
  const { data } = await axios.get<Directory>(
    `${SERVERURL}/project/getRootDirectory/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const addDirectory = async (
  user: Profile,
  parentDirectoryId: number,
  name: string
): Promise<Directory> => {
  const { data } = await axios.post<Directory>(
    `${SERVERURL}/project/directory/${parentDirectoryId}`,
    { name },
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const updateDirectory = async (
  user: Profile,
  directoryId: number,
  name: string
): Promise<Directory> => {
  const { data } = await axios.patch<Directory>(
    `${SERVERURL}/project/directory/${directoryId}`,
    { name },
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const deleteDirectory = async (
  user: Profile,
  directoryId: number
): Promise<number> => {
  const response = await axios.delete(
    `${SERVERURL}/project/directory/${directoryId}`,
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return response.status;
};

export const addFileToDirectory = async (
  user: Profile,
  directoryId: number,
  file: File
): Promise<File> => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await axios.post<File>(
    `${SERVERURL}/project/directory/${directoryId}/file`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    }
  );

  return data;
};

export const updateFile = async (
  user: Profile,
  fileId: number,
  content: {
    base64: string;
    mimeType: string;
    fileName: string;
  }
): Promise<FileEntity> => {
  const { data } = await axios.patch<FileEntity>(
    `${SERVERURL}/project/file/${fileId}`,
    {
      content,
    },
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const moveFile = async (
  user: Profile,
  fileId: number,
  directoryId: number
): Promise<FileEntity> => {
  const { data } = await axios.patch<FileEntity>(
    `${SERVERURL}/project/file/${fileId}/move/${directoryId}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return data;
};

export const deleteFile = async (
  user: Profile,
  fileId: number
): Promise<number> => {
  const response = await axios.delete(
    `${SERVERURL}/project/file/${fileId}`,
    {
      headers: {
        Authorization: `Bearer ${user.token}`,
        "Content-Type": "application/json;charset=UTF-8",
      },
    }
  );

  return response.status;
};